import { displayWorkSummary } from '../../supabase/functions/_shared/workSummary'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Bot, CalendarDays, CheckCircle2, CircleDollarSign, ChevronDown, Mic } from 'lucide-react'

import { activityDateRange, fetchAgentActivity, type WorkActivity } from '../lib/agentActivity'
import { activityLabel } from '../../supabase/functions/_shared/activityDetails'
import { ActivitySummaryStatus } from '../components/ActivitySummaryStatus'
import { supabase, signOut, getCurrentSession } from '../lib/supabase'
import { fetchTeamAgentUsageDaily, sumByDate, toDateKey, type UsageTotals } from '../lib/agentUsage'
import { AppSidebar } from '../components/AppSidebar'
import { TopBar } from '../components/TopBar'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { SidebarInset, SidebarProvider } from '../components/ui/sidebar'
import { Skeleton } from '../components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip'
import { TokenBreakdown, UsageCostFootnote, UsageCostText } from '../components/UsageCost'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'

// ============================================================================
// Page: /dashboard/team/:teamId/standups/:userId
//
// One person's full standup + agent check-in history, day by day, no date
// limit. This used to be a Dialog opened from a card on the Standups page
// (StandupUserCards.tsx) — moved to its own page because "click a card, see
// everything" reads better as navigation than a modal (explicit user call,
// 2026-08-28). See [[syncsimple_frontend_dashboard_v2_restyle]].
//
// Data model: agents.user_id links an agent to the human who owns it, and
// standup_responses.user_id links a standup to the same users table — same
// join this feature has used throughout (see StandupUserCards.tsx).
//
// 2026-10-02 (Step 6 of docs/TOKEN_COST_ACCURACY_PLAN.md): the per-day token
// badge reads get_team_agent_usage_daily (src/lib/agentUsage.ts; input +
// output + cache tokens) instead of summing agent_checkin.tokens_*. Task
// lines, files changed and branches come from get_agent_activity, which
// includes both completed-work check-ins and regular Claude Code activity.
// ============================================================================

interface UserStandupHistoryProps {
  teamId: string
  userId: string
}

interface AgentInfo {
  id: string
  created_at: string | null
  client_id: string | null
  client_name: string | null
  name: string
}

interface HistoryStandupRow {
  date: string
  questions: { yesterday?: string; today?: string; blockers?: string } | null
}

interface HistoryCheckinRow extends WorkActivity {
  checkin_date: string
  task: string
  files_changed: number | null
  git_branch: string | null
}

interface HistoryDay {
  date: string
  standup: HistoryStandupRow | null
  checkins: HistoryCheckinRow[]
  usage: UsageTotals | null
}

type HistoryRange = '7' | '10' | '30' | 'all' | 'custom'

const formatHistoryDate = (dateString: string) =>
  new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

const initialsOf = (name: string) =>
  name
    .split(/[\s-]+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'

interface ProfileStatProps {
  icon: React.ReactNode
  label: string
  value: string
  helper: string
}

const ProfileStat: React.FC<ProfileStatProps> = ({ icon, label, value, helper }) => (
  <div className="border-b border-border bg-background p-4 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
      {icon}
      {label}
    </div>
    <p className="mt-3 text-2xl font-semibold text-foreground">{value}</p>
    <p className="mt-1 text-xs text-muted-foreground">{helper}</p>
  </div>
)

const UserStandupHistory: React.FC<UserStandupHistoryProps> = ({ teamId, userId }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const requestedDate = searchParams.get('date')
  const requestedFrom = searchParams.get('from')
  const requestedTo = searchParams.get('to')
  const requestedRange = activityDateRange(requestedFrom, requestedTo)
  const [teamName, setTeamName] = useState<string | undefined>(undefined)
  const [memberName, setMemberName] = useState<string | null>(null)
  const [memberEmail, setMemberEmail] = useState<string | null>(null)
  const [memberRole, setMemberRole] = useState('Team member')
  const [memberAvatar, setMemberAvatar] = useState<string | null>(null)
  const [profileAgents, setProfileAgents] = useState<AgentInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [days, setDays] = useState<HistoryDay[]>([])
  const [historyRange, setHistoryRange] = useState<HistoryRange>(requestedRange ? 'custom' : '7')
  const [expandedDate, setExpandedDate] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      const session = await getCurrentSession()
      const [teamResult, userResult, agentResult] = await Promise.all([
        supabase.from('teams').select('name').eq('id', teamId).single(),
        supabase.from('users').select('name, email').eq('id', userId).single(),
        // agents_team_visible, not agents directly — see StandupUserCards.tsx
        // for why (team-wide read of display columns + user_id via a view).
        (supabase.from as any)('agents_team_visible')
          .select('id, name, client_id, client_name, created_at')
          .eq('team_id', teamId)
          .eq('user_id', userId),
      ])

      if (cancelled) return

      if (userResult.error || agentResult.error) {
        console.error('Failed to load standup history:', userResult.error ?? agentResult.error)
        setError('Could not load this person’s history.')
        setLoading(false)
        return
      }

      setTeamName(teamResult.data?.name ?? undefined)
      setMemberName(userResult.data?.name || userResult.data?.email?.split('@')[0] || 'Unknown user')
      setMemberEmail(userResult.data?.email ?? null)

      if (session) {
        const { data: teamUsers } = await supabase.functions.invoke('get-all-team-users', {
          body: { team_id: teamId },
          headers: { Authorization: `Bearer ${session.access_token}` },
        })
        const member = (teamUsers?.users ?? []).find((candidate: any) => candidate.user_id === userId)
        if (member) {
          setMemberRole(member.role === 'owner' ? 'Workspace owner' : member.role === 'admin' ? 'Team admin' : member.role === 'manager' ? 'Manager' : 'Team member')
          setMemberAvatar(member.avatar_url ?? null)
        }
      }

      const agents = (agentResult.data ?? []) as AgentInfo[]
      setProfileAgents(agents)
      const agentIds = agents.map((agent) => agent.id)

      const from = requestedRange?.from ?? agents.reduce((earliest, agent) => {
        const created = agent.created_at?.slice(0, 10) ?? '2020-01-01'
        return created < earliest ? created : earliest
      }, toDateKey(new Date()))
      const to = requestedRange?.to ?? toDateKey(new Date())
      let standupsQuery = supabase.from('standup_responses').select('date, questions')
        .eq('team_id', teamId).eq('user_id', userId).order('date', { ascending: false })
      if (requestedRange) standupsQuery = standupsQuery.gte('date', from).lte('date', to)

      const [standupsResult, checkinsResult, usageResult] = await Promise.all([
        standupsQuery,
        Promise.all(agentIds.map(agentId => fetchAgentActivity<HistoryCheckinRow>(teamId, agentId, from, to)))
          .then(data => ({ data: data.flat(), error: null as unknown }),
            (error: unknown) => ({ data: [], error })),
        // "No date limit" history: from the agent's install date to today.
        agentIds.length > 0
          ? fetchTeamAgentUsageDaily(
              teamId,
              from,
              to,
              { agentIds }
            ).then(
              // Only this agent's own rows: an agent the caller can't see in
              // detail comes back with agent_id NULL (never matched here),
              // so the badge just shows no tokens for that person.
              (rows) => ({ byDate: sumByDate(rows.filter((r) => r.agentId && agentIds.includes(r.agentId))), error: null as unknown }),
              (error: unknown) => ({ byDate: new Map<string, UsageTotals>(), error })
            )
          : Promise.resolve({ byDate: new Map<string, UsageTotals>(), error: null as unknown }),
      ])

      if (cancelled) return

      if (standupsResult.error || checkinsResult.error || usageResult.error) {
        console.error(
          'Failed to load standup/checkin rows:',
          standupsResult.error ?? checkinsResult.error ?? usageResult.error
        )
        setError('Could not load this person’s history.')
        setLoading(false)
        return
      }

      const standupRows = (standupsResult.data ?? []) as unknown as HistoryStandupRow[]
      const checkinRows = (checkinsResult.data ?? []) as HistoryCheckinRow[]

      const byDate = new Map<string, HistoryDay>()
      for (const s of standupRows) {
        byDate.set(s.date, { date: s.date, standup: s, checkins: [], usage: null })
      }
      for (const c of checkinRows) {
        const existing = byDate.get(c.checkin_date)
        if (existing) existing.checkins.push(c)
        else byDate.set(c.checkin_date, { date: c.checkin_date, standup: null, checkins: [c], usage: null })
      }
      for (const [date, usage] of usageResult.byDate.entries()) {
        const existing = byDate.get(date)
        if (existing) existing.usage = usage
        else byDate.set(date, { date, standup: null, checkins: [], usage })
      }

      const nextDays = Array.from(byDate.values()).sort((a, b) => (a.date < b.date ? 1 : -1))
      setDays(nextDays)
      if (requestedRange) setHistoryRange('custom')
      if (requestedDate && nextDays.some((day) => day.date === requestedDate)) {
        setHistoryRange('all')
        setExpandedDate(requestedDate)
      }
      setLoading(false)
    }

    load().catch(() => {
      if (!cancelled) { setError('Could not load this person’s history.'); setLoading(false) }
    })
    return () => {
      cancelled = true
    }
  }, [requestedDate, requestedFrom, requestedTo, teamId, userId])

  const goToStandups = () => navigate(`/dashboard/team/${teamId}/standups`)

  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  const visibleDays = useMemo(() => {
    if (historyRange === 'custom' && requestedRange) return days.filter(day => day.date >= requestedRange.from && day.date <= requestedRange.to)
    if (historyRange === 'all') return days
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const start = new Date(today)
    start.setDate(start.getDate() - (Number(historyRange) - 1))
    const startKey = toDateKey(start)
    const todayKey = toDateKey(today)
    return days.filter((day) => day.date >= startKey && day.date <= todayKey)
  }, [days, historyRange, requestedFrom, requestedTo])

  const totalCheckins = visibleDays.reduce((total, day) => total + day.checkins.length, 0)
  const totalStandups = visibleDays.filter((day) => day.standup).length
  const totalUsage = visibleDays.reduce<UsageTotals>(
    (total, day) => {
      if (!day.usage) return total
      return {
        inputTokens: total.inputTokens + day.usage.inputTokens,
        outputTokens: total.outputTokens + day.usage.outputTokens,
        cacheReadTokens: total.cacheReadTokens + day.usage.cacheReadTokens,
        cacheWriteTokens: total.cacheWriteTokens + day.usage.cacheWriteTokens,
        totalTokens: total.totalTokens + day.usage.totalTokens,
        costUsd: total.costUsd === null || day.usage.costUsd === null
          ? total.costUsd ?? day.usage.costUsd
          : total.costUsd + day.usage.costUsd,
        eventCount: total.eventCount + day.usage.eventCount,
        estimatedEventCount: total.estimatedEventCount + day.usage.estimatedEventCount,
        unpricedEventCount: total.unpricedEventCount + day.usage.unpricedEventCount,
      }
    },
    {
      inputTokens: 0,
      outputTokens: 0,
      cacheReadTokens: 0,
      cacheWriteTokens: 0,
      totalTokens: 0,
      costUsd: null,
      eventCount: 0,
      estimatedEventCount: 0,
      unpricedEventCount: 0,
    }
  )
  const activityDays = visibleDays.slice(0, 10)
  const profileInitials = initialsOf(memberName ?? 'User')

  return (
    <SidebarProvider>
      <AppSidebar
        activeSection="standups-checkins"
        onNavigate={goToStandups}
        onOpenTeamManagement={() => navigate(`/dashboard/team/${teamId}/team-management`)}
        onOpenBilling={() => navigate(`/dashboard/team/${teamId}?open=billing`)}
        teamId={teamId}
      />
      <SidebarInset className="min-w-0">
          <TopBar teamId={teamId} teamName={teamName} onLogout={handleLogout} sectionLabel="Standups" />
        <main className="space-y-6 bg-background p-4 sm:p-6">
          <Button
            type="button"
            onClick={goToStandups}
            variant="ghost"
            className="h-auto gap-2 p-0 text-sm font-medium text-primary hover:bg-transparent hover:text-primary/80"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to standups
          </Button>

          {loading ? (
            // Shape mirrors the loaded page below: avatar + name/badge/subtitle
            // header, the 4-cell stat grid, and the two-section check-ins/agents
            // list panel -- so the shimmer reads as "profile incoming" rather
            // than a generic bar. Previously this header rendered unconditionally
            // with its real (but still-empty) fallback text -- "User profile" /
            // "Team member · Team member" / a solid "Active" pill -- flashing
            // before the real data arrived, instead of a skeleton like every
            // other restyled page (TeamManagement, Agents, Summary, Check-ins).
            <div className="space-y-6">
              <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-14 w-14 shrink-0 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-40" />
                    <Skeleton className="h-4 w-56" />
                  </div>
                </div>
                <Skeleton className="h-10 w-full sm:w-36" />
              </div>

              <div className="grid grid-cols-2 border border-border bg-background shadow-sm sm:grid-cols-4">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="space-y-2 p-5">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-7 w-16" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                ))}
              </div>

              <div className="grid border border-border bg-background shadow-sm lg:grid-cols-[1.35fr_1fr]">
                <section className="space-y-5 border-b border-border p-5 lg:border-b-0 lg:border-r">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-36" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <div className="divide-y divide-border">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="flex gap-3 py-4 first:pt-0">
                        <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                        <div className="min-w-0 flex-1 space-y-2">
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
                <section className="space-y-5 p-5">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                  <div className="divide-y divide-border">
                    {[0, 1].map((i) => (
                      <div key={i} className="flex items-center gap-3 py-4 first:pt-0">
                        <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                        <div className="min-w-0 flex-1 space-y-2">
                          <Skeleton className="h-4 w-2/3" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                        <Skeleton className="h-6 w-16 shrink-0 rounded-full" />
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          ) : (
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
              <div className="flex items-center gap-4">
                <Avatar className="h-14 w-14">
                  {memberAvatar && <AvatarImage src={memberAvatar} alt="" />}
                  {/* No explicit bg-* here on purpose: AvatarFallback (src/components/ui/avatar.tsx)
                      derives a deterministic --chart-* token color from the initials when the
                      caller doesn't force one -- consistent with how every other avatar in the
                      app (ProjectDetails, Projects, ProjectsPanel) is colored. */}
                  <AvatarFallback className="text-lg font-semibold">{profileInitials}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-semibold text-foreground">{memberName ?? 'User profile'}</h1>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      Active
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{memberEmail ?? 'Team member'} · {memberRole}</p>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 self-start border border-border bg-background p-1 text-sm text-foreground sm:self-auto">
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
                <Select value={historyRange} onValueChange={(value) => {
                  setHistoryRange(value as HistoryRange)
                  if (requestedRange && value !== 'custom') navigate({ pathname: window.location.pathname, search: '' }, { replace: true })
                }}>
                  <SelectTrigger aria-label="Activity date range" className="h-8 w-[130px] border-0 bg-transparent px-2 shadow-none focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {requestedRange && <SelectItem value="custom">Selected dates</SelectItem>}
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="10">Last 10 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="all">All activity</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
          {!loading && !error && (
            <>
              <div className="grid grid-cols-2 border border-border bg-background shadow-sm sm:grid-cols-4">
                <ProfileStat icon={<Mic className="h-4 w-4" />} label="Human standups" value={totalStandups.toLocaleString()} helper="Submitted in this range" />
                <ProfileStat icon={<CheckCircle2 className="h-4 w-4" />} label="Connector activity" value={totalCheckins.toLocaleString()} helper="Recorded connector updates" />
                <ProfileStat icon={<Bot className="h-4 w-4" />} label="Connectors owned" value={profileAgents.length.toLocaleString()} helper="Connected connectors" />
                <ProfileStat icon={<CircleDollarSign className="h-4 w-4" />} label="Connector spend" value={totalUsage.costUsd === null ? '—' : `$${totalUsage.costUsd.toFixed(2)}`} helper="List-price estimate" />
              </div>

              <div className="grid border border-border bg-background shadow-sm lg:grid-cols-[1.35fr_1fr]">
                <section className="border-b border-border p-5 lg:border-b-0 lg:border-r">
                  <h2 className="text-base font-semibold text-foreground">Daily activity</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Human standups and automated connector work for this member.</p>
                  {historyRange === 'custom' && requestedRange && <p className="mt-1 text-sm text-muted-foreground">{requestedRange.from} to {requestedRange.to}</p>}
                  <div className="mt-5 space-y-3">
                    {activityDays.length === 0 && <p className="py-6 text-sm text-muted-foreground">No activity in this range.</p>}
                    {activityDays.map((day) => {
                      const isExpanded = expandedDate === day.date
                      return (
                        <div key={day.date} className="overflow-hidden border border-border bg-background shadow-sm">
                          <button
                            type="button"
                            onClick={() => setExpandedDate(isExpanded ? null : day.date)}
                            className="w-full space-y-2 px-4 py-4 text-left outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                            aria-expanded={isExpanded}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex min-w-0 items-center gap-3">
                                <time className="text-sm font-semibold text-foreground">{formatHistoryDate(day.date)}</time>
                                <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${day.standup ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                  <span className={`h-1.5 w-1.5 rounded-full ${day.standup ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                  {day.standup ? 'Submitted' : 'Missed'}
                                </span>
                              </div>
                              <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Bot className="mt-0.5 h-4 w-4 shrink-0" />
                              <span>
                                {day.checkins.length > 0
                                  ? `${day.checkins.length} connector ${day.checkins.length === 1 ? 'update' : 'updates'}`
                                  : (day.usage?.eventCount ?? 0) > 0
                                  ? 'Connector usage recorded'
                                  : 'No connector activity'}
                              </span>
                            </div>
                          </button>
                          {isExpanded && (
                            <div className="grid gap-3 border-t border-border bg-muted/30 p-4 text-sm md:grid-cols-2">
                              <div className="border border-border bg-background p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="font-medium text-foreground">Human standup</p>
                                  <Mic className="h-4 w-4 text-muted-foreground" />
                                </div>
                                {day.standup ? (
                                  <div className="mt-3 space-y-3 text-muted-foreground">
                                    <div><p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Yesterday</p><p className="mt-1 leading-5 text-foreground">{day.standup.questions?.yesterday || 'No update provided.'}</p></div>
                                    <div><p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Today</p><p className="mt-1 leading-5 text-foreground">{day.standup.questions?.today || 'No update provided.'}</p></div>
                                    <div><p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Blockers</p><p className="mt-1 leading-5 text-foreground">{day.standup.questions?.blockers || 'None'}</p></div>
                                  </div>
                                ) : (
                                  <div className="mt-3 border border-dashed border-border bg-muted/30 p-3 text-sm text-muted-foreground">No human standup was submitted.</div>
                                )}
                              </div>
                              <div className="border border-border bg-muted/30 p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="font-medium text-foreground">Connector summary</p>
                                  <Bot className="h-4 w-4 text-muted-foreground" />
                                </div>
                                {day.checkins.length > 0 || (day.usage?.eventCount ?? 0) > 0 ? (
                                  <>
                                    {day.checkins.length > 0 ? (
                                      <ul className="mt-3 space-y-2 text-muted-foreground">
                                        {day.checkins.map((checkin, index) => (
                                          <li key={`${day.date}-${index}`} className="leading-5 text-foreground">
                                            <span className="mr-2 text-muted-foreground">•</span>
                                            {displayWorkSummary(checkin.task)}
                                            <span className="block pl-4 text-xs text-muted-foreground">{activityLabel(checkin.classification, checkin.activity_kind)} · {checkin.files_changed ?? 0} files changed{checkin.git_branch ? ` · ${checkin.git_branch}` : ''}</span>
                                            <ActivitySummaryStatus activity={checkin} detailed />
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <p className="mt-3 text-sm text-muted-foreground">Connector usage recorded, but no check-in summary was submitted.</p>
                                    )}
                                    {day.usage && day.usage.eventCount > 0 && (
                                      <div className="mt-4 border-t border-border pt-3">
                                        <div className="flex items-center justify-between text-xs text-muted-foreground"><span>Usage</span><span className="font-medium text-foreground"><UsageCostText totals={day.usage} /></span></div>
                                        <TokenBreakdown totals={day.usage} className="mt-2" />
                                        <UsageCostFootnote totals={day.usage} className="mt-2" />
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="mt-3 border border-dashed border-border bg-background p-3 text-sm text-muted-foreground">No connector activity.</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </section>
                <section className="p-5">
                  <h2 className="text-base font-semibold text-foreground">Owned connectors</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Connectors connected to this member.</p>
                  <div className="mt-5 divide-y divide-border">
                    {profileAgents.length === 0 && <p className="py-6 text-sm text-muted-foreground">No connectors owned.</p>}
                    {profileAgents.map((agent) => (
                      <div key={agent.id} className="flex items-center gap-3 py-4 first:pt-0">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">{initialsOf(agent.name)}</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-foreground">{agent.name}</p>
                          <p className="truncate text-sm text-muted-foreground">{agent.client_name ?? agent.client_id ?? 'Connected connector'}</p>
                        </div>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Active</span>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </>
          )}
        </main>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default UserStandupHistory
